// Einstiegspunkt: Anmeldung, Navigation, Live-Verbindung, Service-Worker.
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/screens.css';

import { registerSW } from 'virtual:pwa-register';
import type { ProductView, ShoppingListResponse } from '@speis/shared';
import { api, consumeTokenFromUrl, currentSession, login } from './core/api.js';
import { guard, installGlobalErrorHandlers } from './core/guard.js';
import { onPendingChange, startOutbox } from './core/outbox.js';
import { connectLive, refreshExpiring } from './core/sse.js';
import {
  applyProducts,
  applyShopping,
  hydrateFromCache,
  setState,
  state,
  subscribe,
} from './core/store.js';
import type { ScreenName } from './core/store.js';
import { applyTheme, initTheme } from './core/theme.js';
import { $, clear, el, haptic } from './ui/dom.js';
import { icon } from './ui/icons.js';
import type { IconName } from './ui/icons.js';
import { toast } from './ui/toast.js';
import { openBarcodeFlow, openMultiScanFlow, openNewProductForm } from './flows/barcode.js';
import { openPantryPhotoFlow } from './flows/photo.js';
import { openReceiptFlow } from './flows/receipt.js';
import { renderFood, teardownFood } from './screens/food.js';
import { renderPantry, refreshProducts, teardownPantry } from './screens/pantry.js';
import { renderPlan, teardownPlan } from './screens/plan.js';
import { renderRecipes, teardownRecipes } from './screens/recipes.js';
import { openSettings } from './screens/settings.js';
import { refreshShopping, renderShopping, teardownShopping } from './screens/shopping.js';

interface ScreenDef {
  name: ScreenName;
  title: string;
  icon: IconName;
  label: string;
  render: (container: HTMLElement, arg?: string) => void;
  teardown: () => void;
}

const SCREENS: ScreenDef[] = [
  {
    name: 'shopping',
    title: 'Einkaufsliste',
    icon: 'cart',
    label: 'Liste',
    render: renderShopping,
    teardown: teardownShopping,
  },
  {
    name: 'pantry',
    title: 'Vorrat',
    icon: 'box',
    label: 'Vorrat',
    render: renderPantry,
    teardown: teardownPantry,
  },
  {
    name: 'plan',
    title: 'Wochenplan',
    icon: 'calendar',
    label: 'Plan',
    render: renderPlan,
    teardown: teardownPlan,
  },
  {
    name: 'recipes',
    title: 'Rezepte',
    icon: 'book',
    label: 'Rezepte',
    render: renderRecipes,
    teardown: teardownRecipes,
  },
  {
    name: 'food',
    title: 'Essen',
    icon: 'apple',
    label: 'Essen',
    render: renderFood,
    teardown: teardownFood,
  },
];

let currentScreen: ScreenDef = SCREENS[0]!;

// ── Gerüst ────────────────────────────────────────────────────────────

function buildShell(): void {
  const app = $('#app')!;
  clear(app);
  app.append(el('div', { class: 'screen', id: 'screen-host' }));

  const nav = $('#bottom-nav')!;
  clear(nav);
  for (const screen of SCREENS) {
    const badge = el('span', { class: 'nav-badge', id: `badge-${screen.name}`, hidden: true });
    nav.append(
      el(
        'button',
        {
          class: 'nav-btn',
          type: 'button',
          'data-screen': screen.name,
          'aria-current': screen.name === currentScreen.name ? 'page' : 'false',
          onclick: () => showScreen(screen.name),
        },
        [icon(screen.icon, { size: 24 }), el('span', {}, screen.label), badge],
      ),
    );
  }
}

function showScreen(name: ScreenName, arg?: string): void {
  const screen = SCREENS.find((s) => s.name === name);
  if (!screen) return;

  currentScreen.teardown();
  currentScreen = screen;
  setState({ screen: name });

  const title = $('#screen-title')!;
  title.textContent = screen.title;
  document.title = `${screen.title} · Speis`;

  for (const button of document.querySelectorAll<HTMLElement>('.nav-btn')) {
    button.setAttribute('aria-current', button.dataset.screen === name ? 'page' : 'false');
  }

  closeFab();
  const host = $('#screen-host')!;
  clear(host);
  screen.render(host, arg);

  // Der Verlauf soll den Zurück-Knopf sinnvoll bedienen.
  const path = name === 'shopping' ? '/' : `/${name}`;
  if (location.pathname !== path) history.pushState({ screen: name }, '', path);
}

function screenFromPath(): { name: ScreenName; arg?: string } {
  const segment = location.pathname.split('/')[1] ?? '';
  const found = SCREENS.find((s) => s.name === segment);
  const arg = new URLSearchParams(location.search).get('filter') ?? undefined;
  return { name: found?.name ?? 'shopping', arg };
}

// ── Aktionsknopf ──────────────────────────────────────────────────────

const FAB_ACTIONS: { key: string; label: string; icon: IconName; run: () => void }[] = [
  { key: 'receipt', label: 'Bon scannen', icon: 'receipt', run: () => void openReceiptFlow() },
  { key: 'photo', label: 'Vorratsfoto', icon: 'camera', run: () => void openPantryPhotoFlow() },
  { key: 'barcode', label: 'Barcode scannen', icon: 'barcode', run: () => void openBarcodeFlow() },
  { key: 'multi', label: 'Einkauf einscannen', icon: 'box', run: () => void openMultiScanFlow() },
  { key: 'new', label: 'Produkt anlegen', icon: 'plus', run: openNewProductForm },
];

function buildFab(): void {
  const menu = $('#fab-menu')!;
  clear(menu);
  for (const action of FAB_ACTIONS) {
    menu.append(
      el(
        'button',
        {
          type: 'button',
          onclick: () => {
            closeFab();
            action.run();
          },
        },
        [icon(action.icon, { size: 20 }), action.label],
      ),
    );
  }

  const fab = $('#fab')!;
  fab.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = fab.getAttribute('aria-expanded') === 'true';
    fab.setAttribute('aria-expanded', String(!open));
    menu.hidden = open;
    haptic(8);
  });

  document.addEventListener('click', (e) => {
    if (menu.hidden) return;
    const target = e.target as HTMLElement;
    if (target.closest('#fab-menu') || target.closest('#fab')) return;
    closeFab();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !menu.hidden) closeFab();
  });
}

function closeFab(): void {
  const fab = $('#fab');
  const menu = $('#fab-menu');
  if (!fab || !menu) return;
  fab.setAttribute('aria-expanded', 'false');
  menu.hidden = true;
}

// ── Statusanzeigen ────────────────────────────────────────────────────

function wireStatus(): void {
  const dot = $('#conn-dot')!;
  const bar = $('#offline-bar')!;

  const update = (): void => {
    const online = state.online && navigator.onLine;
    dot.dataset.state = online ? 'online' : 'offline';
    dot.title = online ? 'Verbunden' : 'Offline – Änderungen werden nachgeholt';

    const pending = state.pendingOps;
    bar.hidden = online && pending === 0;
    clear(bar);
    if (!bar.hidden) {
      bar.append(
        icon(online ? 'refresh' : 'alert', { size: 16 }),
        el(
          'span',
          {},
          online
            ? `${pending} Änderung${pending === 1 ? '' : 'en'} werden übertragen …`
            : pending
              ? `Offline – ${pending} Änderung${pending === 1 ? '' : 'en'} warten`
              : 'Offline – Änderungen werden gespeichert und später übertragen',
        ),
      );
    }
  };

  subscribe((changed) => {
    if (changed.has('online') || changed.has('pendingOps')) update();
    if (changed.has('expiring')) updateExpiryBadge();
  });

  onPendingChange((count) => setState({ pendingOps: count }));
  window.addEventListener('online', () => setState({ online: true }));
  window.addEventListener('offline', () => setState({ online: false }));
  update();
}

function updateExpiryBadge(): void {
  const badge = $('#badge-pantry');
  if (!badge) return;
  const count = state.expiring.filter((i) => i.daysLeft <= 3).length;
  badge.hidden = count === 0;
  badge.textContent = String(count);
}

// ── Anmeldung ─────────────────────────────────────────────────────────

function showGate(error?: string): void {
  const gate = $('#token-gate')!;
  gate.hidden = false;
  const errorBox = $('#token-error')!;
  errorBox.hidden = !error;
  errorBox.textContent = error ?? '';
  $<HTMLInputElement>('#token-input')?.focus();
}

function wireGate(): void {
  const form = $<HTMLFormElement>('#token-form')!;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = $<HTMLInputElement>('#token-input')!;
    const value = input.value.trim();
    if (!value) return;

    void guard(
      async () => {
        const me = await login(value);
        setState({ me });
        if (me.theme) applyTheme(me.theme);
        input.value = '';
        $('#token-gate')!.hidden = true;
        await start();
      },
      { failure: 'Anmeldung fehlgeschlagen' },
    ).then((ok) => {
      if (ok === null) showGate('Token ungültig oder Server nicht erreichbar.');
    });
  });
}

// ── Start ─────────────────────────────────────────────────────────────

async function start(): Promise<void> {
  await hydrateFromCache();

  // Ohne Profil bleiben Einstellungen und Essens-Tab funktionslos.
  if (!state.me) {
    const session = await currentSession().catch(() => null);
    if (session) setState({ me: session.me });
  }

  const { name, arg } = screenFromPath();
  currentScreen = SCREENS.find((s) => s.name === name) ?? SCREENS[0]!;
  buildShell();
  const host = $('#screen-host')!;
  $('#screen-title')!.textContent = currentScreen.title;
  currentScreen.render(host, arg);

  connectLive();
  startOutbox();

  // Frische Daten holen; scheitert das (offline), bleibt der Cache stehen.
  await Promise.allSettled([
    api<ShoppingListResponse>('/api/shopping').then(applyShopping),
    api<{ products: ProductView[] }>('/api/products').then((r) => applyProducts(r.products)),
    refreshExpiring(),
  ]);
  updateExpiryBadge();
}

async function init(): Promise<void> {
  installGlobalErrorHandlers();
  initTheme();
  wireGate();
  buildFab();
  wireStatus();

  $('#settings-btn')!.addEventListener('click', () => void openSettings());

  window.addEventListener('popstate', () => {
    const { name, arg } = screenFromPath();
    if (name !== currentScreen.name) showScreen(name, arg);
  });

  // Sitzung abgelaufen -> zurück zum Tor.
  window.addEventListener('speis:unauthorized', () => showGate('Bitte neu anmelden.'));

  // Erst-Anmeldung per Link.
  if (await consumeTokenFromUrl()) {
    await start();
    return;
  }

  const session = await currentSession().catch(() => null);
  if (session) {
    setState({ me: session.me });
    if (session.me.theme) applyTheme(session.me.theme);
    await start();
  } else {
    showGate();
  }
}

// ── Service-Worker ────────────────────────────────────────────────────

const updateSW = registerSW({
  onNeedRefresh() {
    toast('Neue Version verfügbar', {
      ms: 15_000,
      action: { label: 'Neu laden', onClick: () => void updateSW(true) },
    });
  },
  onOfflineReady() {
    // Nur beim allerersten Mal interessant; sonst wäre es Lärm.
    if (!localStorage.getItem('speis_offline_ready')) {
      localStorage.setItem('speis_offline_ready', '1');
      toast('Speis funktioniert jetzt auch offline.');
    }
  },
});

// Mitteilung angetippt -> passenden Bildschirm öffnen.
navigator.serviceWorker?.addEventListener('message', (event: MessageEvent) => {
  const data = event.data as { type?: string; url?: string } | null;
  if (data?.type !== 'navigate' || !data.url) return;
  const url = new URL(data.url, location.origin);
  const segment = url.pathname.split('/')[1] ?? '';
  const screen = SCREENS.find((s) => s.name === segment);
  if (screen) showScreen(screen.name, url.searchParams.get('filter') ?? undefined);
});

void init();

export { refreshProducts, refreshShopping, showScreen };
